import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Clock, Calendar, FileText } from "lucide-react";
import axios from "axios";
import { usePageSeo, BASE_URL } from "@/hooks/useSeo";
import Navigation from "@/components/Navigation";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BlogIndexPage() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  usePageSeo({
    title: "Blog | LAB336 – Insights zu Digitalisierung, KI & Prozessoptimierung",
    description: "Aktuelle Artikel zu Digitalisierung, KI-Integration und Prozessoptimierung für Schweizer KMU. Praxiswissen von LAB336.",
    canonical: `${BASE_URL}/blog`,
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "CollectionPage",
          "name": "LAB336 Blog",
          "description": "Aktuelle Artikel zu Digitalisierung, KI-Integration und Prozessoptimierung",
          "url": `${BASE_URL}/blog`,
          "publisher": { "@type": "Organization", "name": "LAB336", "url": BASE_URL }
        },
        {
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": BASE_URL },
            { "@type": "ListItem", "position": 2, "name": "Blog", "item": `${BASE_URL}/blog` }
          ]
        }
      ]
    }
  });

  useEffect(() => {
    window.scrollTo(0, 0);
    axios.get(`${API}/blog`)
      .then(res => setPosts(res.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleDateString("de-CH", { day: "numeric", month: "long", year: "numeric" });
  };

  return (
    <div className="min-h-screen bg-white" data-testid="blog-index-page">
      <Navigation />
      <header className="bg-lab-bg-dark pt-24">
        <div className="max-w-5xl mx-auto px-6 lg:px-8 py-12">
          <Link to="/" className="inline-flex items-center gap-2 text-gray-400 hover:text-lab-accent transition-colors text-sm mb-8" data-testid="back-to-home">
            <ArrowLeft className="w-4 h-4" /> Zurück zur Startseite
          </Link>
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-white/10 flex items-center justify-center">
              <FileText className="w-7 h-7 text-lab-accent" />
            </div>
            <div>
              <h1 className="font-playfair text-3xl lg:text-4xl font-semibold text-white">Blog</h1>
              <p className="text-gray-400 mt-1">Insights zu Digitalisierung, KI & Prozessoptimierung</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-16">
        {loading ? (
          <div className="text-center py-16">
            <div className="animate-pulse text-lab-text-muted">Artikel werden geladen...</div>
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-lab-text-muted text-lg">Noch keine Artikel vorhanden.</p>
            <Link to="/" className="text-lab-accent hover:underline mt-4 inline-block">Zurück zur Startseite</Link>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-10">
            {posts.map((post, index) => (
              <motion.article
                key={post.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="group"
                data-testid={`blog-card-${post.seo?.slug || post.id}`}
              >
                <Link to={`/blog/${post.seo?.slug || post.id}`} className="block">
                  <div className="relative aspect-[16/10] overflow-hidden mb-5 bg-lab-bg-light">
                    <img
                      src={post.image_url}
                      alt={post.title}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "https://images.unsplash.com/photo-1677442136019-21780ecad995?w=800&q=80";
                      }}
                    />
                  </div>
                  <div className="flex items-center gap-4 text-xs text-lab-text-muted mb-3">
                    {post.category && (
                      <span className="bg-lab-accent/20 text-lab-primary px-2 py-0.5 font-semibold tracking-wide">
                        {post.category}
                      </span>
                    )}
                    {post.date && (
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" /> {formatDate(post.date)}
                      </span>
                    )}
                    {post.read_time && (
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" /> {post.read_time}
                      </span>
                    )}
                  </div>
                  <h2 className="font-playfair text-xl font-semibold text-lab-primary group-hover:text-lab-accent transition-colors duration-200 mb-2">
                    {post.title}
                  </h2>
                  <p className="text-lab-text-muted text-sm leading-relaxed line-clamp-3">
                    {post.excerpt}
                  </p>
                </Link>
              </motion.article>
            ))}
          </div>
        )}
      </main>

      <footer className="bg-lab-bg-dark py-8 px-6 text-center">
        <Link to="/" className="font-playfair text-lg text-white tracking-wide">
          LAB<span className="text-lab-accent">336</span>
        </Link>
        <p className="text-gray-500 text-xs mt-2">Beratung für Prozessoptimierung, Digitalisierung und KI-Integration</p>
      </footer>
    </div>
  );
}
